import { template } from 'lodash';
import get from 'lodash/get';
// eslint-disable-next-line import/no-cycle
import { validateGraterThanOne, validateLength } from './utils';
import { getTableFields, NONE, TABLE_FIELDS } from './constants';

export const MAP_FORMAT = {
  DELIMITED: 'DELIMITED',
  FIXED: 'FIXED'
};

export const FIXED_FORMAT_HEADER_FIELDS = {
  RECORD_LENGTH: 'recordLength'
};

export const RECORD_LENGTH_FIELD_SETTINGS = {
  MAX_VALUE: 9999,
  MAX_LENGTH: 4
};
export const START_POSITION_MAX_LENGTH = 4;
export const LENGTH_MAX_LENGTH = 2;
export const IMPORT_FILE_MAX_LENGTH = 10;

export const HARDCODED_LENGTH_FIELDS_LENGTH = {
  [TABLE_FIELDS.TRAN_CODE]: 1,
  [TABLE_FIELDS.BANK_CODE]: 1,
  [TABLE_FIELDS.DISC_DATA]: 1
};

export const getFixedFormatTableFields = (
  locale,
  includeOptionalFields = false,
  excludeAddValues = false
) =>
  getTableFields(locale).reduce(
    (acc, item) => [
      ...acc,
      ...(!item.optional || includeOptionalFields
        ? [
            `${item.id}_startPosition`,
            `${item.id}_length`,
            ...(item.addValue && !excludeAddValues ? [`${item.id}_addValue`] : [])
          ]
        : [])
    ],
    []
  );

export const getDelimitedFormatTableFields = (
  locale,
  excludeAddValues = false,
  includeOptionalFields = false
) =>
  getTableFields(locale).reduce(
    (acc, item) => [
      ...acc,
      ...(!item.optional || includeOptionalFields
        ? [item.id, ...(item.addValue && !excludeAddValues ? [`${item.id}_addValue`] : [])]
        : [])
    ],
    []
  );

export const recordLengthValidators = (locale) => [
  { required: true, msg: locale['validation.required'] },
  validateLength(RECORD_LENGTH_FIELD_SETTINGS.MAX_LENGTH, locale),
  validateGraterThanOne(locale)
];

export const validateLengthTableField = (locale, fixedValue) => ({
  fn: (name, _, data) =>
    data[name] && Number(data[name]) !== Number(fixedValue)
      ? template(locale['wireImports.valueMustBeEqual'])({ fixedValue })
      : true
});

// validation

const getFixedFieldsValidatorsData = ({
  lengthFieldValidators,
  startPositionFieldValidators,
  tableFieldData
}) => [
  {
    // startPosition
    name: `${tableFieldData.id}_startPosition`,
    validator: startPositionFieldValidators(tableFieldData)
  },
  {
    // length
    name: `${tableFieldData.id}_length`,
    validator: lengthFieldValidators(tableFieldData)
  }
];

export const registerFixedFieldValidators = ({
  lengthFieldValidators,
  registerValidators,
  startPositionFieldValidators,
  tableFieldData
}) => {
  const fixedFieldsValidatorsData = getFixedFieldsValidatorsData({
    tableFieldData,
    startPositionFieldValidators,
    lengthFieldValidators
  });

  return fixedFieldsValidatorsData.forEach((item) =>
    registerValidators(item.name, item.validator)
  );
};

const fieldValidationRegistration = ({
  dynamicRegisterAddValueValidator,
  dynamicRegisterPositionValidator,
  field,
  formState,
  mapFormat
}) => {
  const tranCodeNotSelected =
    get(formState[`${TABLE_FIELDS.TRAN_CODE}_addValue`], '[0].id') === NONE ||
    !formState[`${TABLE_FIELDS.TRAN_CODE}_addValue`];

  if (mapFormat === MAP_FORMAT.DELIMITED) {
    if (
      (field.addValue && !formState[field.id]) ||
      (field.id === TABLE_FIELDS.TRAN_CODE && !tranCodeNotSelected)
    ) {
      dynamicRegisterAddValueValidator();
    }
  }

  if (mapFormat === MAP_FORMAT.FIXED) {
    if (
      field.addValue &&
      !formState[`${field.id}_startPosition`] &&
      !formState[`${field.id}_length`]
    ) {
      dynamicRegisterAddValueValidator();
    }
  }

  if (!formState[`${field.id}_addValue`]) {
    dynamicRegisterPositionValidator();
  }

  return null;
};

export const setInitialFieldsValidation = ({
  addValueValidators,
  formState,
  lengthFieldValidators,
  mapFormat,
  positionFieldValidators,
  registerValidators,
  startPositionFieldValidators,
  tableFields
}) => {
  return tableFields.forEach((field) => {
    // get (position) or (startPosition & length) registrators - depending on MAP_FORMAT
    const dynamicRegisterDelimitedFieldValidators = () =>
      registerValidators(field.id, positionFieldValidators(field));

    const dynamicRegisterFixedFieldValidators = () =>
      registerFixedFieldValidators({
        tableFieldData: field,
        startPositionFieldValidators,
        lengthFieldValidators,
        registerValidators
      });

    const dynamicRegisterPositionValidator =
      mapFormat === MAP_FORMAT.DELIMITED
        ? dynamicRegisterDelimitedFieldValidators
        : dynamicRegisterFixedFieldValidators;

    // get AddValue registrator
    const dynamicRegisterAddValueValidator = () =>
      registerValidators(`${field.id}_addValue`, addValueValidators(field, field?.maxLength));

    return fieldValidationRegistration({
      field,
      mapFormat,
      dynamicRegisterPositionValidator,
      dynamicRegisterAddValueValidator,
      formState
    });
  });
};
